<template>
  <div class="skin-card">
    <NuxtLink
      :aria-label="'Link to skin' + item.localed_name"
      :to="
        localePath(
          getItemLinkWithoutExt({
            slug: item.link,
            name: item.market_hash_name
          })
        )
      "
      class="skin-link"
    >
    </NuxtLink>

    <Cases :cases="(item.crates as CratesItem[]) || []" compressed />

    <div class="skin-image">
      <Thumb
          :fontSize="13"
          :height="15"
          :item="item"
          :size="15"
          :src="getImageFromWaxpeer(
              item.link.includes('sunset-storm') ?
              item.market_hash_name + '-factory-new' :
               item.link)"
      />
    </div>

    <div class="skin-info">
      <span
        :class="{
          'is-stattrak': item.is_stattrak,
          'is-souvenir': item.is_souvenir
        }"
        class="skin-brand"
      >
        {{ getWeaponOnly(item.localed_name) }}
      </span>
      <p v-if="getMarketName(item.localed_name)" class="skin-name">
        {{ getMarketName(item.localed_name) }}
      </p>
    </div>
    <div v-if="item.min_price" class="skin-prices">
      <UiPrice :price="item.min_price" />
      <template v-if="item.max_price">
        -
        <UiPrice :price="item.max_price" />
      </template>
    </div>

    <client-only>
      <div v-if="item.offers" class="skin-offers f fc gap8">
        <span>
          {{ t("offers_count", { count: getOffersCount(item.offers) }) }}
        </span>
        <span> {{ t("offers_on_markets", { count: item.markets }) }}</span>
      </div>
      <div v-else class="skin-offers">
        {{ t("No offers") }}
      </div>
    </client-only>
  </div>
</template>

<script lang="ts" setup>
import Thumb from "~/components/Pages/Item/Components/Children/Thumb.vue";
import { useI18n } from "vue-i18n";
import { useResize } from "~/composables/resize.js";
import type { AdvancedSearchBody, CratesItem } from "~/graphql/generated/schema-types";
import { getImageFromWaxpeer } from "~/utils/get-image-url";
import { getWeaponOnly } from "~/utils/name-utils";
import Cases from "~/components/Pages/Weapon/Components/Cases.vue";

const props = defineProps<{
  item: AdvancedSearchBody;
}>();

const { t } = useI18n();
const currW = useResize();
const localePath = useLocalePath();

const getMarketName = (name: string): string => {
  const match = name.match(/\|\s*([^\(|]+)\s*(\(|$)/);
  return match ? match[1].trim() : "";
};

const getOffersCount = (num: number): string => {
  if (num < 1000) {
    return num.toString();
  }
  if (num < 1000000) {
    return Intl.NumberFormat("en", { notation: "compact" }).format(num / 1000) + "K";
  }
  return Intl.NumberFormat("en", { notation: "compact" }).format(num / 1000000) + "M";
};
</script>

<style lang="scss" scoped>
@use "sass:color";

.skin-card {
  padding: 16px 16px 34px;
  background: var(--surfaces-low-container);
  border-radius: 1.2rem;
  display: flex;
  flex-direction: column;
  //gap: 0.8rem;
  position: relative;
  transition: background-color 0.5s ease;

  &:deep(.thumb) {
    padding: 16px;

    img {
      transition: 0.5s scale;
    }
  }

  &:hover,
  &:focus {
    outline: none;
    background: color-mix(in srgb, var(--surfaces-low-container) 98%, white);
    border-radius: 12px 12px 0 0;
    //z-index: 5;

    .skin-offers {
      //transform: translateY(30px);
      opacity: 1;
    }

    :deep(.thumb) img {
      scale: 1.1;
    }
  }

  .skin-image:deep(.thumb) {
    padding: 0;
    margin-bottom: 16px;

    img {
      width: 15rem;
    }
  }

  .skin-link {
    width: 100%;
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  .skin-info {
    margin: auto 0 0;
    display: flex;
    flex-direction: column;
    text-align: center;

    .skin-collection {
      .collection-name {
        overflow: hidden;
        text-align: center;
        font-kerning: none;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        font-weight: 400;
        color: var(--accent-secondary);
        margin-bottom: 16px;
        display: inline-block;
      }
    }

    .skin-name {
      color: var(--surfaces-on-surface);
      text-align: center;
      font-kerning: none;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 22px;
      margin-bottom: 16px;
    }

    .skin-brand {
      display: inline-block;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 28px;
      color: var(--surfaces-on-surface-variable);
      margin-bottom: 4px;

      &.is-souvenir {
        color: var(--souvenir);
      }

      &.is-stattrak {
        color: var(--stattrak);
      }
    }
  }

  .skin-prices {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    text-align: center;
    color: var(--surfaces-on-surface-variable);

    :deep(.price) {
      color: var(--surfaces-on-surface-variable);
      text-align: center;
      font-kerning: none;
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
    }
  }

  .skin-offers {
    position: absolute;
    top: calc(100%);
    left: 0;
    right: 0;
    z-index: 5;
    transition: all ease 0.5s;
    opacity: 0;
    background: var(--accent-secondary);
    border-radius: 0 0 12px 12px;
    font-size: 14px;
    font-weight: 400;
    text-align: center;
    padding: 3px;
  }
}
</style>
